// src/pages/admin/members/AdminAllMembers.tsx
//
// Full member roster across every role and tier.
//
// ACCESS:
//   admin, committee_chair  — search, filter, edit, deactivate, nominate
//   portal_manager, committee_member — read-only list; can advance quarter from panel
//   regional_rep            — read-only list; can nominate their own mentees
//
// ACTIONS CAPTURED:
//   PATCH /admin/users/:id                  — edit region, province, ministryRole, role (admin), tier (chair)
//   DELETE /admin/users/:id                 — soft-deactivate (admin only)
//   PATCH /admin/mentees/:id/advance-quarter — bypass task check (content roles)
//   PATCH /admin/mentees/:id/set-tier        — force tier (admin only)
//   POST  /fellows/nominate                  — nominate mentee (rep / admin)

import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Users,
  EyeOff,
  Pencil,
  Star,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Flame,
  Info,
} from "lucide-react";

import {
  CAN_VIEW_USERS,
  CAN_EDIT_USERS,
  CAN_DEACTIVATE,
  CAN_NOMINATE,
  TIER_META,
  ROLE_META,
  getRoleMeta,
  getTierMeta,
  QUARTERS,
  PAGE_SIZE,
  Bone,
  FL,
  Textarea,
  Sel,
  Avatar,
  ConfirmModal,
  EditMemberPanel,
  StatTile,
} from "./MemberShared";
import {
  ManagedUser,
  useDeactivateUserMutation,
  useGetUsersQuery,
} from "../../app/api/adminApi";
import { QuarterKey } from "../../app/api/programmeApi";
import { useAppSelector } from "../../app/hooks";
import { MemberTier, UserRole } from "../../app/api/authApi";
import { useNominateFellowMutation } from "../../app/api/fellowsApi";

/* ─────────────────────────────────────────────────────────────────
   NOMINATE MODAL
───────────────────────────────────────────────────────────────── */
const NominateModal: React.FC<{
  mentee: ManagedUser;
  isLoading: boolean;
  apiError: string | null;
  onConfirm: (quarter: QuarterKey, comment: string) => void;
  onCancel: () => void;
}> = ({ mentee, isLoading, apiError, onConfirm, onCancel }) => {
  const [quarter, setQuarter] = useState<QuarterKey>("Q1");
  const [comment, setComment] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <Star size={18} className="text-amber-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Nominate for Legacy Fellows
            </h3>
            <p className="text-xs text-gray-500">
              {mentee.firstName} {mentee.lastName}
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div>
            <FL required>Quarter</FL>
            <Sel
              value={quarter}
              onChange={(e) => setQuarter(e.target.value as QuarterKey)}
            >
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Sel>
          </div>

          <div>
            <FL>
              Nomination note{" "}
              <span className="font-normal text-gray-400">(optional)</span>
            </FL>
            <Textarea
              rows={3}
              placeholder="Why are you nominating this member?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>

        {apiError && (
          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-3">
            {apiError}
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(quarter, comment)}
            disabled={isLoading}
            className="flex-1 py-2 text-sm font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
          >
            {isLoading ? "Nominating…" : "Nominate"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MEMBER ROW
───────────────────────────────────────────────────────────────── */
const MemberRow: React.FC<{
  member: ManagedUser;
  canEdit: boolean;
  canDeactivate: boolean;
  canNominate: boolean;
  onEdit: (m: ManagedUser) => void;
  onDeactivate: (m: ManagedUser) => void;
  onNominate: (m: ManagedUser) => void;
}> = ({
  member,
  canEdit,
  canDeactivate,
  canNominate,
  onEdit,
  onDeactivate,
  onNominate,
}) => {
  const roleMeta: { label: string; colour: string } = getRoleMeta(member.role);
  const tierMeta: { label: string; colour: string } = getTierMeta(member.tier);

  return (
    <div
      className={`flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors ${
        !member.isActive ? "opacity-50" : ""
      }`}
    >
      <Avatar user={member} size="sm" />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {member.firstName} {member.lastName}
          </p>

          {!member.isActive && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 shrink-0">
              Inactive
            </span>
          )}

          {member.fellowsEligible && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 shrink-0 flex items-center gap-0.5">
              <Flame size={8} />
              Fellows
            </span>
          )}
        </div>

        <p className="text-[11px] text-gray-400 truncate">{member.email}</p>
      </div>

      <div className="hidden md:block shrink-0 text-right w-32">
        <p className="text-[11px] text-gray-500 truncate">
          {member.region?.split("—")[1]?.trim() ?? member.region ?? "—"}
        </p>
      </div>

      <div className="hidden sm:block shrink-0">
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${roleMeta.colour}`}
        >
          {roleMeta.label}
        </span>
      </div>

      <div className="hidden lg:block shrink-0 w-30 text-right">
        {member.role === "mentee" && (
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tierMeta.colour}`}
          >
            {tierMeta.label}
          </span>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1">
        {canNominate && member.role === "mentee" && member.isActive && (
          <button
            onClick={() => onNominate(member)}
            title="Nominate for Legacy Fellows"
            className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 transition-colors"
          >
            <Star size={14} />
          </button>
        )}

        {canEdit && (
          <button
            onClick={() => onEdit(member)}
            title="Edit member"
            className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
          >
            <Pencil size={14} />
          </button>
        )}

        {canDeactivate && member.isActive && (
          <button
            onClick={() => onDeactivate(member)}
            title="Deactivate"
            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <EyeOff size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PAGE
───────────────────────────────────────────────────────────────── */
const AdminAllMembers: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;

  const canView = CAN_VIEW_USERS.includes(role);
  const canEdit = CAN_EDIT_USERS.includes(role);
  const canDeactivate = CAN_DEACTIVATE.includes(role);
  const canNominate = CAN_NOMINATE.includes(role);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [editTarget, setEditTarget] = useState<ManagedUser | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<ManagedUser | null>(
    null,
  );
  const [nominateTarget, setNominateTarget] = useState<ManagedUser | null>(
    null,
  );
  const [nominateErr, setNominateErr] = useState<string | null>(null);

  const queryArgs = useMemo(
    () => ({
      ...(roleFilter !== "all" ? { role: roleFilter as UserRole } : {}),
      ...(tierFilter !== "all" ? { tier: tierFilter as MemberTier } : {}),
      ...(activeFilter !== "all"
        ? { isActive: activeFilter === "active" }
        : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      limit: PAGE_SIZE,
      page,
    }),
    [roleFilter, tierFilter, activeFilter, search, page],
  );

  const {
    data: usersRes,
    isLoading,
    isFetching,
  } = useGetUsersQuery(queryArgs, {
    skip: !canView,
  });

  const [deactivateUser, { isLoading: isDeactivating }] =
    useDeactivateUserMutation();
  const [nominateFellow, { isLoading: isNominating }] =
    useNominateFellowMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const members = usersRes?.data ?? [];
  const totalCount = usersRes?.total ?? 0;
  const totalPages = usersRes?.pages ?? 1;

  useEffect(() => {
    setPage(1);
  }, [roleFilter, tierFilter, activeFilter, search]);

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    try {
      await deactivateUser(deactivateTarget.id).unwrap();
      setDeactivateTarget(null);
    } catch {
      // ignore
    }
  };

  const handleNominate = async (quarter: QuarterKey, comment: string) => {
    if (!nominateTarget) return;

    setNominateErr(null);

    try {
      await nominateFellow({
        menteeId: nominateTarget.id,
        quarter,
        nominationComment: comment || undefined,
      }).unwrap();

      setNominateTarget(null);
    } catch (err: any) {
      setNominateErr(
        err?.data?.error ?? "Could not nominate. Please try again.",
      );
    }
  };

  const tierCounts = useMemo(
    () => ({
      wider: members.filter((m) => m.tier === "wider_audience").length,
      circle: members.filter((m) => m.tier === "inner_circle").length,
      fellows: members.filter((m) => m.tier === "legacy_fellows").length,
    }),
    [members],
  );

  if (!canView) {
    return (
      <div className="space-y-4 pb-12">
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
          <Info size={13} className="shrink-0" />
          {role === "regional_rep"
            ? "You have read-only access. Only Admin and Committee Chair can manage accounts."
            : "Member management requires Admin or Committee Chair access."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Total members"
          value={totalCount}
          colour="text-purple-700"
          icon={<Users size={12} className="text-purple-400" />}
          loading={isLoading}
        />
        <StatTile
          label="Wider Audience"
          value={tierCounts.wider}
          colour="text-green-700"
          icon={<span className="w-2 h-2 rounded-full bg-green-400" />}
        />
        <StatTile
          label="Inner Circle"
          value={tierCounts.circle}
          colour="text-blue-700"
          icon={<span className="w-2 h-2 rounded-full bg-blue-400" />}
        />
        <StatTile
          label="Legacy Fellows"
          value={tierCounts.fellows}
          colour="text-purple-700"
          icon={<span className="w-2 h-2 rounded-full bg-purple-400" />}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
          />
        </div>

        <Sel
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="sm:w-40"
        >
          <option value="all">All roles</option>
          {Object.entries(ROLE_META).map(([r, m]) => (
            <option key={r} value={r}>
              {m.label}
            </option>
          ))}
        </Sel>

        <Sel
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="sm:w-40"
        >
          <option value="all">All tiers</option>
          {Object.entries(TIER_META).map(([t, m]) => (
            <option key={t} value={t}>
              {m.label}
            </option>
          ))}
        </Sel>

        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
          {(["all", "active", "inactive"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setActiveFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                activeFilter === s
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-bold text-gray-700">
            {isLoading
              ? "Loading…"
              : `${totalCount.toLocaleString()} member${
                  totalCount !== 1 ? "s" : ""
                }`}
          </p>

          {isFetching && !isLoading && (
            <RefreshCw size={12} className="text-gray-400 animate-spin" />
          )}
        </div>

        {isLoading ? (
          <div className="p-4 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <Bone key={i} className="h-14" />
            ))}
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
            <Users size={24} className="text-gray-300" />
            <p className="text-sm font-semibold text-gray-600">
              No members found
            </p>
            <p className="text-xs text-gray-400">Try adjusting the filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                canEdit={canEdit}
                canDeactivate={canDeactivate}
                canNominate={canNominate}
                onEdit={setEditTarget}
                onDeactivate={setDeactivateTarget}
                onNominate={setNominateTarget}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-[11px] text-gray-400">
              Page {page} of {totalPages}
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {editTarget && (
        <EditMemberPanel
          member={editTarget}
          callerRole={role}
          onClose={() => setEditTarget(null)}
        />
      )}

      {deactivateTarget && (
        <ConfirmModal
          title={`Deactivate ${deactivateTarget.firstName} ${deactivateTarget.lastName}?`}
          message={`${deactivateTarget.email} will lose access immediately. This can be reversed.`}
          confirmLabel="Deactivate"
          danger
          isLoading={isDeactivating}
          onConfirm={handleDeactivate}
          onCancel={() => setDeactivateTarget(null)}
        />
      )}

      {nominateTarget && (
        <NominateModal
          mentee={nominateTarget}
          isLoading={isNominating}
          apiError={nominateErr}
          onConfirm={handleNominate}
          onCancel={() => {
            setNominateTarget(null);
            setNominateErr(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminAllMembers;
